import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Shield, UserCheck, Users, Crown, GraduationCap, Settings, Search, Filter, School, ToggleLeft, ToggleRight, Clock, CalendarIcon, X, CheckSquare, Square } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRole, UserRole } from '@/hooks/useRole';
import { toast } from 'sonner';
import { format, addDays, addWeeks, addMonths } from 'date-fns';
import { cn } from '@/lib/utils';

interface UserWithRole {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  department?: string;
  position_title?: string;
  school_name?: string;
  current_role?: UserRole;
  role_granted_at?: string;
  role_granted_by?: string;
  role_expires_at?: string;
}

interface RoleManagerProps {
  users: UserWithRole[];
  onRoleUpdate: () => void;
}

interface Permission {
  id: string;
  permission: string;
  description: string;
  role: UserRole;
}

const roleColors = {
  admin: 'bg-red-100 text-red-800 border-red-300',
  coordinator: 'bg-purple-100 text-purple-800 border-purple-300',
  principal: 'bg-orange-100 text-orange-800 border-orange-300',
  instructor: 'bg-blue-100 text-blue-800 border-blue-300',
  mentor: 'bg-green-100 text-green-800 border-green-300',
  user: 'bg-gray-100 text-gray-800 border-gray-300'
};

const roleIcons = {
  admin: Crown,
  coordinator: Shield,
  principal: UserCheck,
  instructor: UserCheck,
  mentor: GraduationCap,
  user: Users
};

const roleDescriptions = {
  admin: 'Full system administration and all privileges',
  coordinator: 'Course approval, instructor management, reports',
  principal: 'School-level management, user oversight, school reports',
  instructor: 'Course creation, attendance, participant communication',
  mentor: 'New teacher mentoring, meeting tracking, progress reports',
  user: 'Course participation and portfolio management'
};

export function RoleManager({ users, onRoleUpdate }: RoleManagerProps) {
  const { user: currentUser } = useAuth();
  const { role: currentUserRole, isAdmin, isCoordinator } = useRole();
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole>('user');
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showBulkRoleDialog, setShowBulkRoleDialog] = useState(false);
  const [isUberAdmin, setIsUberAdmin] = useState(false);
  const [expirationDate, setExpirationDate] = useState<Date | undefined>(undefined);
  const [isTimeLimited, setIsTimeLimited] = useState(false);
  const [permissions, setPermissions] = useState<Record<UserRole, Permission[]>>({
    admin: [],
    coordinator: [],
    principal: [],
    instructor: [],
    mentor: [],
    user: []
  });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [schoolFilter, setSchoolFilter] = useState<string>('all');
  const [filteredUsers, setFilteredUsers] = useState<UserWithRole[]>(users);
  const [availableSchools, setAvailableSchools] = useState<string[]>([]);
  
  // Privilege toggle mode
  const [privilegeMode, setPrivilegeMode] = useState(false);
  const [userPermissions, setUserPermissions] = useState<Record<string, string[]>>({});
  const [optimisticRoles, setOptimisticRoles] = useState<Record<string, UserRole>>({});
  
  // Multi-select states
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  useEffect(() => {
    checkUberAdminStatus();
    fetchPermissions();
    loadUserPermissions();
    
    // Extract unique schools
    const schools = Array.from(new Set(users.map(user => user.school_name).filter(Boolean)));
    setAvailableSchools(schools);
    
    // Clear optimistic updates when real data arrives with updated roles
    setOptimisticRoles(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(userId => {
        const user = users.find(u => u.id === userId);
        if (user && user.current_role === updated[userId]) {
          delete updated[userId];
        }
      });
      return updated;
    });
  }, [users]);
  
  const checkUberAdminStatus = async () => {
    if (!currentUser?.id) return;
    
    const { data, error } = await supabase.rpc('is_uber_admin');
    if (!error && data) {
      setIsUberAdmin(true);
    }
  };

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, schoolFilter]);

  const filterUsers = () => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(user => 
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.position_title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.current_role === roleFilter);
    }

    if (schoolFilter !== 'all') {
      filtered = filtered.filter(user => user.school_name === schoolFilter);
    }

    setFilteredUsers(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setSchoolFilter('all');
    setSelectedUserIds(new Set());
  };

  // Multi-select handlers
  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === filteredUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const clearSelection = () => {
    setSelectedUserIds(new Set());
  };

  const getSelectableUsers = () => {
    return filteredUsers.filter(user => 
      isUberAdmin || isAdmin || (isCoordinator && user.current_role !== 'admin' && user.current_role !== 'coordinator')
    );
  };

  const handleBulkAssignRole = async () => {
    if (!currentUser?.id || selectedUserIds.size === 0) return;

    setBulkProcessing(true);
    try {
      if (!canAssignRole(selectedRole)) {
        toast.error('You do not have permission to assign this role');
        return;
      }

      const expiresAt = isTimeLimited && expirationDate ? expirationDate.toISOString() : null;
      let successCount = 0;
      let failCount = 0;

      for (const userId of selectedUserIds) {
        try {
          // Deactivate existing roles for this user
          const { error: deactivateError } = await supabase
            .from('user_roles')
            .update({ is_active: false })
            .eq('user_id', userId);

          if (deactivateError) throw deactivateError;

          // Check if this user_id + role combination already exists
          const { data: existingRole } = await supabase
            .from('user_roles')
            .select('id')
            .eq('user_id', userId)
            .eq('role', selectedRole)
            .maybeSingle();

          if (existingRole) {
            const { error: updateError } = await supabase
              .from('user_roles')
              .update({ 
                is_active: true,
                granted_by: currentUser.id,
                granted_at: new Date().toISOString(),
                expires_at: expiresAt
              })
              .eq('id', existingRole.id);

            if (updateError) throw updateError;
          } else {
            const { error: insertError } = await supabase
              .from('user_roles')
              .insert({
                user_id: userId,
                role: selectedRole,
                granted_by: currentUser.id,
                is_active: true,
                expires_at: expiresAt
              });

            if (insertError) throw insertError;
          }
          successCount++;
        } catch (error) {
          console.error(`Error assigning role to user ${userId}:`, error);
          failCount++;
        }
      }

      const expirationMsg = expiresAt 
        ? ` (expires ${format(new Date(expiresAt), 'MMM dd, yyyy')})`
        : '';
      
      if (failCount === 0) {
        toast.success(`Successfully assigned ${selectedRole} role to ${successCount} user${successCount !== 1 ? 's' : ''}${expirationMsg}`);
      } else {
        toast.warning(`Assigned role to ${successCount} users, ${failCount} failed${expirationMsg}`);
      }

      setShowBulkRoleDialog(false);
      setSelectedUserIds(new Set());
      setIsTimeLimited(false);
      setExpirationDate(undefined);
      onRoleUpdate();
      loadUserPermissions();
    } catch (error) {
      console.error('Error in bulk role assignment:', error);
      toast.error('Failed to assign roles');
    } finally {
      setBulkProcessing(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('id, role, permission, description')
        .order('role', { ascending: false });

      if (error) throw error;

      const permissionsByRole = data?.reduce((acc, item) => {
        if (!acc[item.role as UserRole]) {
          acc[item.role as UserRole] = [];
        }
        acc[item.role as UserRole].push(item);
        return acc;
      }, {
        admin: [],
        coordinator: [],
        principal: [],
        instructor: [],
        user: []
      } as Record<UserRole, Permission[]>) || {
        admin: [],
        coordinator: [],
        principal: [],
        instructor: [],
        mentor: [],
        user: []
      };

      setPermissions(permissionsByRole);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const loadUserPermissions = async () => {
    try {
      const userIds = users.map(u => u.id);
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', userIds)
        .eq('is_active', true);

      const userPerms: Record<string, string[]> = {};
      
      rolesData?.forEach(roleData => {
        const rolePermissions = permissions[roleData.role as UserRole] || [];
        userPerms[roleData.user_id] = rolePermissions.map(p => p.permission);
      });

      setUserPermissions(userPerms);
    } catch (error) {
      console.error('Error loading user permissions:', error);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUser || !currentUser?.id) return;

    try {
      // Check if user can assign this role
      if (!canAssignRole(selectedRole)) {
        toast.error('You do not have permission to assign this role');
        return;
      }

      const expiresAt = isTimeLimited && expirationDate ? expirationDate.toISOString() : null;

      // Deactivate existing roles for this user
      const { error: deactivateError } = await supabase
        .from('user_roles')
        .update({ is_active: false })
        .eq('user_id', selectedUser.id);

      if (deactivateError) throw deactivateError;

      // Check if this user_id + role combination already exists
      const { data: existingRole } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', selectedUser.id)
        .eq('role', selectedRole)
        .maybeSingle();

      if (existingRole) {
        // Role record exists, just reactivate it
        const { error: updateError } = await supabase
          .from('user_roles')
          .update({ 
            is_active: true,
            granted_by: currentUser.id,
            granted_at: new Date().toISOString(),
            expires_at: expiresAt
          })
          .eq('id', existingRole.id);

        if (updateError) throw updateError;
      } else {
        // No existing record, insert new one
        const { error: insertError } = await supabase
          .from('user_roles')
          .insert({
            user_id: selectedUser.id,
            role: selectedRole,
            granted_by: currentUser.id,
            is_active: true,
            expires_at: expiresAt
          });

        if (insertError) throw insertError;
      }

      const expirationMsg = expiresAt 
        ? ` (expires ${format(new Date(expiresAt), 'MMM dd, yyyy')})`
        : '';
      toast.success(`Successfully assigned ${selectedRole} role to ${selectedUser.first_name} ${selectedUser.last_name}${expirationMsg}`);
      setShowRoleDialog(false);
      setSelectedUser(null);
      setIsTimeLimited(false);
      setExpirationDate(undefined);
      onRoleUpdate();
      loadUserPermissions();
    } catch (error) {
      console.error('Error assigning role:', error);
      toast.error('Failed to assign role');
    }
  };

  const toggleUserPrivilege = async (userId: string, newRole: UserRole, currentRole?: UserRole) => {
    if (!currentUser?.id) return;

    console.log('🔄 toggleUserPrivilege called:', { userId, newRole, currentRole, currentUserId: currentUser.id });

    // Optimistically update the UI
    setOptimisticRoles(prev => ({ ...prev, [userId]: newRole }));

    try {
      // Check if user can assign this role
      if (!canAssignRole(newRole)) {
        console.log('❌ Cannot assign role:', newRole);
        toast.error('You do not have permission to assign this role');
        // Revert optimistic update
        setOptimisticRoles(prev => {
          const updated = { ...prev };
          delete updated[userId];
          return updated;
        });
        return;
      }

      console.log('✅ Permission check passed, deactivating existing roles...');

      // Deactivate all existing roles for this user
      const { error: deactivateError } = await supabase
        .from('user_roles')
        .update({ is_active: false })
        .eq('user_id', userId);

      if (deactivateError) {
        console.error('❌ Deactivate error:', deactivateError);
        throw deactivateError;
      }

      console.log('✅ Roles deactivated, checking if role record exists...');

      // Check if this user_id + role combination already exists
      const { data: existingRole } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', userId)
        .eq('role', newRole)
        .maybeSingle();

      if (existingRole) {
        // Role record exists, just reactivate it
        console.log('♻️ Reactivating existing role record');
        const { error: updateError } = await supabase
          .from('user_roles')
          .update({ 
            is_active: true,
            granted_by: currentUser.id,
            granted_at: new Date().toISOString()
          })
          .eq('id', existingRole.id);

        if (updateError) {
          console.error('❌ Update error:', updateError);
          throw updateError;
        }
      } else {
        // No existing record, insert new one
        console.log('➕ Inserting new role record');
        const { error: insertError } = await supabase
          .from('user_roles')
          .insert({
            user_id: userId,
            role: newRole,
            granted_by: currentUser.id,
            is_active: true
          });

        if (insertError) {
          console.error('❌ Insert error:', insertError);
          throw insertError;
        }
      }

      const user = users.find(u => u.id === userId);
      console.log('✅ Role updated successfully to:', newRole);
      toast.success(`Successfully updated privileges for ${user?.first_name} ${user?.last_name}`);
      
      // Trigger parent refresh
      onRoleUpdate();
      loadUserPermissions();
      
      // Keep optimistic update - it will be replaced when real data loads
    } catch (error) {
      console.error('❌ Error toggling privilege:', error);
      toast.error('Failed to update privileges');
      // Revert optimistic update on error
      setOptimisticRoles(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
    }
  };

  const canAssignRole = (role: UserRole): boolean => {
    if (isUberAdmin) return true; // Uber admins can assign any role across all districts
    if (isAdmin) return true; // District admins can assign any role in their district
    if (isCoordinator && (role === 'principal' || role === 'instructor' || role === 'mentor' || role === 'user')) return true;
    return false;
  };

  const getAvailableRoles = (): UserRole[] => {
    if (isUberAdmin || isAdmin) return ['admin', 'coordinator', 'principal', 'instructor', 'mentor', 'user'];
    if (isCoordinator) return ['principal', 'instructor', 'mentor', 'user'];
    return [];
  };

  const getRoleBadge = (role: UserRole | undefined) => {
    if (!role) return <Badge variant="outline">No Role</Badge>;
    
    const Icon = roleIcons[role];
    return (
      <Badge className={roleColors[role]}>
        <Icon className="h-3 w-3 mr-1" />
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  const openRoleDialog = (user: UserWithRole) => {
    setSelectedUser(user);
    setSelectedRole(user.current_role || 'user');
    setIsTimeLimited(!!user.role_expires_at);
    setExpirationDate(user.role_expires_at ? new Date(user.role_expires_at) : undefined);
    setShowRoleDialog(true);
  };

  const handleQuickExpiration = (option: string) => {
    const today = new Date();
    switch (option) {
      case '1week':
        setExpirationDate(addWeeks(today, 1));
        break;
      case '2weeks':
        setExpirationDate(addWeeks(today, 2));
        break;
      case '1month':
        setExpirationDate(addMonths(today, 1));
        break;
      case '3months':
        setExpirationDate(addMonths(today, 3));
        break;
      case '6months':
        setExpirationDate(addMonths(today, 6));
        break;
      case '1year':
        setExpirationDate(addMonths(today, 12));
        break;
      case 'end_of_semester':
        // Assume semester ends in December or May
        const month = today.getMonth();
        if (month < 5) {
          setExpirationDate(new Date(today.getFullYear(), 4, 31)); // May 31
        } else if (month < 11) {
          setExpirationDate(new Date(today.getFullYear(), 11, 20)); // Dec 20
        } else {
          setExpirationDate(new Date(today.getFullYear() + 1, 4, 31)); // Next May 31
        }
        break;
      case 'end_of_year':
        // School year typically ends in June
        const currentMonth = today.getMonth();
        if (currentMonth < 5) {
          setExpirationDate(new Date(today.getFullYear(), 5, 15)); // June 15
        } else {
          setExpirationDate(new Date(today.getFullYear() + 1, 5, 15)); // Next June 15
        }
        break;
    }
    setIsTimeLimited(true);
  };

  const getAllPermissions = () => {
    const allPerms = new Set<string>();
    Object.values(permissions).forEach(rolePerms => {
      rolePerms.forEach(perm => allPerms.add(perm.permission));
    });
    return Array.from(allPerms);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Role Management
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label htmlFor="privilege-mode">Privilege Mode</Label>
                <Switch
                  id="privilege-mode"
                  checked={privilegeMode}
                  onCheckedChange={setPrivilegeMode}
                />
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Filters */}
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search users..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="coordinator">Coordinator</SelectItem>
                    <SelectItem value="principal">Principal</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={schoolFilter} onValueChange={setSchoolFilter}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Filter by school" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Schools</SelectItem>
                    {availableSchools.map(school => (
                      <SelectItem key={school} value={school}>{school}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={clearFilters}>
                  <Filter className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>

            {!privilegeMode ? (
              <>
                {/* Role Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {Object.entries(roleDescriptions).map(([role, description]) => {
                    const Icon = roleIcons[role as UserRole];
                    const userCount = filteredUsers.filter(u => u.current_role === role).length;
                    
                    return (
                      <Card key={role} className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-4 w-4" />
                          <span className="font-medium">{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                          <Badge variant="outline">{userCount}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{description}</p>
                      </Card>
                    );
                  })}
                </div>

                {/* Bulk Actions Bar */}
                {selectedUserIds.size > 0 && (
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckSquare className="h-5 w-5 text-primary" />
                      <span className="text-sm font-medium">
                        {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearSelection}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Clear
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedRole('user');
                          setIsTimeLimited(false);
                          setExpirationDate(undefined);
                          setShowBulkRoleDialog(true);
                        }}
                      >
                        <Users className="h-4 w-4 mr-1" />
                        Change Roles
                      </Button>
                    </div>
                  </div>
                )}

                {/* User Role Table */}
                <div className="border rounded-lg">
                  <div className="p-4 border-b bg-muted/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedUserIds.size > 0 && selectedUserIds.size === getSelectableUsers().length}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all users"
                      />
                      <h3 className="text-sm font-medium">User Roles ({filteredUsers.length})</h3>
                    </div>
                    {filteredUsers.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Click checkboxes to select multiple users
                      </p>
                    )}
                  </div>
                  <div className="divide-y">
                    {filteredUsers.map((user) => {
                      const canSelect = isUberAdmin || isAdmin || (isCoordinator && user.current_role !== 'admin' && user.current_role !== 'coordinator');
                      return (
                        <div 
                          key={user.id} 
                          className={cn(
                            "p-4 flex items-center justify-between transition-colors",
                            selectedUserIds.has(user.id) && "bg-primary/5"
                          )}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {canSelect && (
                              <Checkbox
                                checked={selectedUserIds.has(user.id)}
                                onCheckedChange={() => toggleUserSelection(user.id)}
                                aria-label={`Select ${user.first_name} ${user.last_name}`}
                              />
                            )}
                            {!canSelect && <div className="w-4" />}
                            <div>
                              <p className="font-medium">{user.first_name} {user.last_name}</p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                              {user.department && (
                                <p className="text-xs text-muted-foreground">{user.department} • {user.position_title}</p>
                              )}
                              {user.school_name && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <School className="h-3 w-3" />
                                  {user.school_name}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col items-end gap-1">
                              {getRoleBadge(user.current_role)}
                              {user.role_expires_at && (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Expires {format(new Date(user.role_expires_at), 'MMM dd, yyyy')}
                                </Badge>
                              )}
                            </div>
                            {canSelect && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openRoleDialog(user)}
                              >
                                Change Role
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              /* Privilege Management Mode */
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-800 mb-1">Privilege Management Mode</h3>
                  <p className="text-xs text-blue-700">The toggle icons show current permissions. To change a user's role, use the <strong>dropdown menu in the Actions column</strong>.</p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-border">
                    <thead>
                      <tr>
                        <th className="border border-border p-2 bg-muted text-left">User</th>
                        <th className="border border-border p-2 bg-muted text-left">Current Role</th>
                        {getAllPermissions().slice(0, 5).map(permission => (
                          <th key={permission} className="border border-border p-2 bg-muted text-center text-xs">
                            {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </th>
                        ))}
                        <th className="border border-border p-2 bg-muted text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => {
                        const userPerms = userPermissions[user.id] || [];
                        return (
                          <tr key={user.id}>
                            <td className="border border-border p-2">
                              <div>
                                <p className="font-medium text-sm">{user.first_name} {user.last_name}</p>
                                <p className="text-xs text-muted-foreground">{user.email}</p>
                              </div>
                            </td>
                            <td className="border border-border p-2">
                              {getRoleBadge(optimisticRoles[user.id] || user.current_role)}
                            </td>
                             {getAllPermissions().slice(0, 5).map(permission => {
                              const hasPermission = userPerms.includes(permission);
                              return (
                                <td key={permission} className="border border-border p-2 text-center">
                                  <div className="flex items-center justify-center">
                                    {hasPermission ? (
                                      <ToggleRight className="h-5 w-5 text-green-600" />
                                    ) : (
                                      <ToggleLeft className="h-5 w-5 text-gray-400" />
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {hasPermission ? 'Active' : 'Inactive'}
                                  </p>
                                </td>
                              );
                            })}
                            <td className="border border-border p-2 text-center">
                              <Select
                                value={optimisticRoles[user.id] || user.current_role || 'user'}
                                onValueChange={(value) => toggleUserPrivilege(user.id, value as UserRole, user.current_role)}
                                disabled={!(isUberAdmin || isAdmin || (isCoordinator && user.current_role !== 'admin' && user.current_role !== 'coordinator'))}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {getAvailableRoles().map(role => {
                                    const Icon = roleIcons[role];
                                    return (
                                      <SelectItem key={role} value={role}>
                                        <div className="flex items-center gap-2">
                                          <Icon className="h-3 w-3" />
                                          {role.charAt(0).toUpperCase() + role.slice(1)}
                                        </div>
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredUsers.length > 10 && (
                    <p className="text-sm text-muted-foreground mt-2 p-2">
                      Showing first 10 users of {filteredUsers.length} total
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Role Assignment Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Role</DialogTitle>
            <DialogDescription>
              Change the role for {selectedUser?.first_name} {selectedUser?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Select Role</label>
              <Select value={selectedRole} onValueChange={(value) => setSelectedRole(value as UserRole)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableRoles().map((role) => (
                    <SelectItem key={role} value={role}>
                      <div className="flex items-center gap-2">
                        {React.createElement(roleIcons[role], { className: "h-4 w-4" })}
                        <span>{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-sm font-medium mb-1">Role Description:</p>
              <p className="text-sm text-muted-foreground">{roleDescriptions[selectedRole]}</p>
            </div>

            {/* Time-Limited Role Section */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="time-limited" className="text-sm font-medium">Time-Limited Role</Label>
                </div>
                <Switch
                  id="time-limited"
                  checked={isTimeLimited}
                  onCheckedChange={(checked) => {
                    setIsTimeLimited(checked);
                    if (!checked) setExpirationDate(undefined);
                  }}
                />
              </div>
              
              {isTimeLimited && (
                <div className="space-y-3 pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    Role will automatically revert to "User" after the expiration date.
                  </p>
                  
                  {/* Quick Duration Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickExpiration('1week')}
                    >
                      1 Week
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickExpiration('1month')}
                    >
                      1 Month
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickExpiration('3months')}
                    >
                      3 Months
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickExpiration('end_of_semester')}
                    >
                      End of Semester
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickExpiration('end_of_year')}
                    >
                      End of School Year
                    </Button>
                  </div>

                  {/* Custom Date Picker */}
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal flex-1",
                            !expirationDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {expirationDate ? format(expirationDate, "PPP") : "Pick expiration date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={expirationDate}
                          onSelect={setExpirationDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {expirationDate && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setExpirationDate(undefined)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {expirationDate && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                      <p className="text-xs text-amber-800">
                        <strong>Expires:</strong> {format(expirationDate, "PPPP")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-2">Permissions Include:</p>
              <div className="text-xs text-blue-700 space-y-1 max-h-40 overflow-y-auto pr-2">
                {permissions[selectedRole]?.length > 0 ? (
                  permissions[selectedRole].map((permission) => (
                    <div key={permission.id} className="flex items-start gap-2 py-1">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium">{permission.permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                        {permission.description && (
                          <p className="text-blue-600/80">{permission.description}</p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-blue-600/80 italic">No specific permissions defined for this role</p>
                )}
              </div>
              {permissions[selectedRole]?.length > 0 && (
                <p className="text-xs text-blue-600 mt-2 pt-2 border-t border-blue-200">
                  Total: {permissions[selectedRole].length} permission{permissions[selectedRole].length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>
              Cancel
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button>Assign Role</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Role Assignment</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to assign the {selectedRole} role to {selectedUser?.first_name} {selectedUser?.last_name}? 
                    {isTimeLimited && expirationDate && (
                      <span className="block mt-2 font-medium text-amber-700">
                        This role will expire on {format(expirationDate, "PPPP")}.
                      </span>
                    )}
                    This will replace their current role and update their system permissions.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleAssignRole}>
                    Assign Role
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Role Assignment Dialog */}
      <Dialog open={showBulkRoleDialog} onOpenChange={setShowBulkRoleDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Role Assignment</DialogTitle>
            <DialogDescription>
              Assign a role to {selectedUserIds.size} selected user{selectedUserIds.size !== 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Selected Users Preview */}
            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-sm font-medium mb-2">Selected Users:</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {Array.from(selectedUserIds).map(userId => {
                  const user = users.find(u => u.id === userId);
                  return user ? (
                    <div key={userId} className="flex items-center justify-between text-sm">
                      <span>{user.first_name} {user.last_name}</span>
                      <div className="flex items-center gap-2">
                        {getRoleBadge(user.current_role)}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => toggleUserSelection(userId)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Select New Role</label>
              <Select value={selectedRole} onValueChange={(value) => setSelectedRole(value as UserRole)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableRoles().map((role) => (
                    <SelectItem key={role} value={role}>
                      <div className="flex items-center gap-2">
                        {React.createElement(roleIcons[role], { className: "h-4 w-4" })}
                        <span>{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-sm font-medium mb-1">Role Description:</p>
              <p className="text-sm text-muted-foreground">{roleDescriptions[selectedRole]}</p>
            </div>

            {/* Time-Limited Role Section */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="bulk-time-limited" className="text-sm font-medium">Time-Limited Role</Label>
                </div>
                <Switch
                  id="bulk-time-limited"
                  checked={isTimeLimited}
                  onCheckedChange={(checked) => {
                    setIsTimeLimited(checked);
                    if (!checked) setExpirationDate(undefined);
                  }}
                />
              </div>
              
              {isTimeLimited && (
                <div className="space-y-3 pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    Role will automatically revert to "User" after the expiration date.
                  </p>
                  
                  {/* Quick Duration Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickExpiration('1week')}
                    >
                      1 Week
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickExpiration('1month')}
                    >
                      1 Month
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickExpiration('3months')}
                    >
                      3 Months
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickExpiration('end_of_semester')}
                    >
                      End of Semester
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickExpiration('end_of_year')}
                    >
                      End of School Year
                    </Button>
                  </div>

                  {/* Custom Date Picker */}
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal flex-1",
                            !expirationDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {expirationDate ? format(expirationDate, "PPP") : "Pick expiration date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={expirationDate}
                          onSelect={setExpirationDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    {expirationDate && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setExpirationDate(undefined)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {expirationDate && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                      <p className="text-xs text-amber-800">
                        <strong>Expires:</strong> {format(expirationDate, "PPPP")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setShowBulkRoleDialog(false)}>
              Cancel
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={bulkProcessing}>
                  {bulkProcessing ? 'Processing...' : `Assign to ${selectedUserIds.size} User${selectedUserIds.size !== 1 ? 's' : ''}`}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Bulk Role Assignment</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to assign the <strong>{selectedRole}</strong> role to <strong>{selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''}</strong>?
                    {isTimeLimited && expirationDate && (
                      <span className="block mt-2 font-medium text-amber-700">
                        This role will expire on {format(expirationDate, "PPPP")}.
                      </span>
                    )}
                    <span className="block mt-2">
                      This will replace their current roles and update their system permissions.
                    </span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleBulkAssignRole} disabled={bulkProcessing}>
                    {bulkProcessing ? 'Processing...' : 'Assign Roles'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}